/**
 * Publish Page - Manage ready-to-publish and published posts
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../platform';
import {
  Copy,
  Download,
  CheckCircle,
  ExternalLink,
  Calendar,
  Link as LinkIcon,
  Send,
  Clock,
  Linkedin,
  Sparkles,
  Loader2,
  AlertTriangle,
  XCircle,
  ArrowRight,
  X,
} from 'lucide-react';
import { api, type PostDraft, type LinkedInConnection } from '../lib/api';

const ATTRIBUTION_TEXT = '\n\n---\nWritten with Mazo';

export function PublishPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [readyPosts, setReadyPosts] = useState<PostDraft[]>([]);
  const [publishedPosts, setPublishedPosts] = useState<PostDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Publish modal
  const [publishingPost, setPublishingPost] = useState<PostDraft | null>(null);
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [publishNotes, setPublishNotes] = useState('');

  // LinkedIn formatter modal
  const [formattingPost, setFormattingPost] = useState<PostDraft | null>(null);
  const [linkedInFormatted, setLinkedInFormatted] = useState('');

  // LinkedIn auto-publish state
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInConnection | null>(null);
  const [publishingToLinkedIn, setPublishingToLinkedIn] = useState<string | null>(null);
  const [linkedInSuccess, setLinkedInSuccess] = useState<string | null>(null);

  // LinkedIn publish confirmation dialog
  const [confirmPublishPost, setConfirmPublishPost] = useState<PostDraft | null>(null);
  const [linkedInPromoDismissed, setLinkedInPromoDismissed] = useState(
    () => localStorage.getItem('mazo_linkedin_promo_dismissed') === 'true'
  );

  useEffect(() => {
    if (isAuthenticated) {
      loadPosts();
      // Fetch LinkedIn connection status
      api.getLinkedInStatus().then((result) => {
        if (result.data) {
          const liData = (result.data as any)?.data || result.data;
          setLinkedInStatus(liData);
        }
      }).catch(() => {
        // LinkedIn status unavailable - leave as null
      });
    }
  }, [isAuthenticated]);

  const loadPosts = async () => {
    setLoading(true);
    const [readyResult, publishedResult] = await Promise.all([
      api.listPosts({ status: 'ready_to_publish' }),
      api.listPosts({ status: 'published' }),
    ]);

    if (readyResult.data) {
      setReadyPosts(readyResult.data.data);
    }
    if (publishedResult.data) {
      setPublishedPosts(publishedResult.data.data);
    }
    setLoading(false);
  };

  const handleCopy = async (post: PostDraft) => {
    const textToCopy = post.show_attribution !== false
      ? post.body + ATTRIBUTION_TEXT
      : post.body;
    await navigator.clipboard.writeText(textToCopy);
    setCopiedId(post.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExport = (post: PostDraft, format: 'txt' | 'md') => {
    const content = post.show_attribution !== false
      ? post.body + ATTRIBUTION_TEXT
      : post.body;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `post-${post.id.slice(0, 8)}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePublish = async () => {
    if (!publishingPost) return;

    const postToPublish = publishingPost;

    const result = await api.publishPost(postToPublish.id, {
      url: linkedInUrl || undefined,
      notes: publishNotes || undefined,
    });

    if (result.data) {
      setPublishingPost(null);
      setLinkedInUrl('');
      setPublishNotes('');
      // Reload both lists from server to avoid stale data from auth-triggered remount race
      await loadPosts();
    } else if (result.error) {
      setError(result.error.message);
    }
  };

  const handlePublishToLinkedIn = async (post: PostDraft) => {
    setPublishingToLinkedIn(post.id);
    setError(null);

    try {
      const result = await api.publishToLinkedIn(post.id);
      if (result.data) {
        setLinkedInSuccess(post.id);
        setTimeout(() => setLinkedInSuccess(null), 3000);
        // Reload posts to reflect the new status
        await loadPosts();
      } else if (result.error) {
        if (result.error.code === 'LINKEDIN_TOKEN_EXPIRED') {
          setError('LinkedIn token expired. Please reconnect in Settings.');
          // Update local status to reflect expiry
          setLinkedInStatus(prev => prev ? { ...prev, status: 'expired' } : null);
        } else {
          setError(result.error.message);
        }
      }
    } catch {
      setError('Failed to publish to LinkedIn. Please try again.');
    }

    setPublishingToLinkedIn(null);
  };

  const isLinkedInActive = linkedInStatus?.connected && linkedInStatus.status === 'active';
  const isLinkedInExpiring = isLinkedInActive && linkedInStatus?.daysUntilExpiry != null && linkedInStatus.daysUntilExpiry <= 14;
  const isLinkedInExpired = linkedInStatus?.connected && linkedInStatus?.status === 'expired';

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatForLinkedIn = (text: string): string => {
    // Split into paragraphs
    const paragraphs = text.split('\n\n').filter(p => p.trim());

    let formatted = '';

    // Add hook/intro with extra spacing
    if (paragraphs[0]) {
      formatted += paragraphs[0].trim() + '\n\n';
    }

    // Process middle paragraphs with better spacing
    for (let i = 1; i < paragraphs.length; i++) {
      const para = paragraphs[i].trim();

      // Check if it's a list item or bullet point
      if (para.startsWith('-') || para.startsWith('•') || para.startsWith('*')) {
        // Keep list formatting but ensure spacing
        formatted += para + '\n';
      } else {
        // Regular paragraph with LinkedIn-friendly spacing
        formatted += para + '\n\n';
      }
    }

    // Add extra line before closing/CTA if there is one
    formatted = formatted.trim() + '\n\n';

    // Extract and format hashtags
    const hashtagRegex = /#\w+/g;
    const hashtags = text.match(hashtagRegex) || [];
    const uniqueHashtags = [...new Set(hashtags)];

    // Remove inline hashtags from the main text
    let cleanedText = formatted;
    uniqueHashtags.forEach(tag => {
      cleanedText = cleanedText.replace(new RegExp(tag + '\\s*', 'g'), '');
    });

    // Add hashtags at the end
    if (uniqueHashtags.length > 0) {
      cleanedText = cleanedText.trim() + '\n\n' + '—\n' + uniqueHashtags.join(' ');
    }

    return cleanedText.trim();
  };

  const handleLinkedInFormat = (post: PostDraft) => {
    let formatted = formatForLinkedIn(post.body);
    // Append attribution if enabled
    if (post.show_attribution !== false) {
      formatted = formatted + ATTRIBUTION_TEXT;
    }
    setLinkedInFormatted(formatted);
    setFormattingPost(post);
  };

  const handleCopyFormatted = async () => {
    await navigator.clipboard.writeText(linkedInFormatted);
    setCopiedId(formattingPost?.id || null);
    setTimeout(() => {
      setCopiedId(null);
      setFormattingPost(null);
    }, 1500);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to manage publishing.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Publish</h1>
        <p className="text-gray-500 mt-1">
          Copy, export, and track your published posts
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* LinkedIn connect prompt — shown to users who haven't connected yet */}
      {!linkedInPromoDismissed && linkedInStatus && !linkedInStatus.connected && (
        <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-blue-50">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0A66C2] via-[#0A66C2] to-[#0A66C2]" />
          <div className="p-5 flex items-start gap-4">
            <div className="p-2.5 bg-[#0A66C2] rounded-lg flex-shrink-0">
              <Linkedin className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">New: Publish directly to LinkedIn</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Skip the copy-paste — connect your LinkedIn account and publish posts with one click.
              </p>
              <button
                onClick={() => navigate('/settings')}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#0A66C2] text-white text-sm font-medium rounded-lg hover:bg-[#094d92] transition-colors"
              >
                Connect LinkedIn
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              onClick={() => {
                localStorage.setItem('mazo_linkedin_promo_dismissed', 'true');
                setLinkedInPromoDismissed(true);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors flex-shrink-0"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* LinkedIn token expiring banner */}
      {isLinkedInExpiring && (
        <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800 flex-1">
            Your LinkedIn token expires in {linkedInStatus?.daysUntilExpiry} days.{' '}
            <button
              onClick={() => navigate('/settings')}
              className="underline hover:text-yellow-900 font-medium"
            >
              Re-authenticate in Settings
            </button>
          </p>
        </div>
      )}

      {/* LinkedIn token expired banner */}
      {isLinkedInExpired && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">
            LinkedIn connection expired.{' '}
            <button
              onClick={() => navigate('/settings')}
              className="underline hover:text-red-900 font-medium"
            >
              Reconnect in Settings
            </button>{' '}
            to publish directly.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-400" />
        </div>
      ) : (
        <>
          {/* Ready to Publish */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-mazo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Ready to Publish</h2>
              <span className="px-2 py-0.5 bg-mazo-100 text-mazo-700 rounded-full text-sm">
                {readyPosts.length}
              </span>
            </div>

            {readyPosts.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed rounded-lg p-8 text-center">
                <p className="text-gray-500">No posts ready to publish.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Mark drafts as "Ready" in the Posts section.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {readyPosts.map((post) => (
                  <div key={post.id} className="bg-white border rounded-lg p-4">
                    <div className="flex gap-4">
                      {/* Post preview */}
                      <div className="flex-1">
                        {post.title && (
                          <p className="font-medium text-gray-900 mb-1">{post.title}</p>
                        )}
                        <p className="text-gray-900 whitespace-pre-wrap line-clamp-4">
                          {post.body}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <span>{post.body.length} chars</span>
                          <span>•</span>
                          <span>Created {formatDate(post.created_at)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {isLinkedInActive ? (
                          <button
                            onClick={() => setConfirmPublishPost(post)}
                            disabled={publishingToLinkedIn === post.id}
                            className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-60"
                            style={{ backgroundColor: publishingToLinkedIn === post.id ? '#0A66C2' : '#0A66C2' }}
                            onMouseEnter={(e) => { if (publishingToLinkedIn !== post.id) (e.currentTarget.style.backgroundColor = '#004182'); }}
                            onMouseLeave={(e) => { (e.currentTarget.style.backgroundColor = '#0A66C2'); }}
                          >
                            {publishingToLinkedIn === post.id ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Publishing...
                              </>
                            ) : linkedInSuccess === post.id ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                Published!
                              </>
                            ) : (
                              <>
                                <Linkedin className="h-4 w-4" />
                                Publish to LinkedIn
                              </>
                            )}
                          </button>
                        ) : !linkedInStatus?.connected ? (
                          <button
                            onClick={() => navigate('/settings')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-gray-400 border border-dashed rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            title="Connect LinkedIn in Settings"
                          >
                            <Linkedin className="h-4 w-4" />
                            Connect LinkedIn in Settings
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleLinkedInFormat(post)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2]/10 text-[#0A66C2] rounded-lg hover:bg-[#0A66C2]/20 transition-colors"
                        >
                          <Linkedin className="h-4 w-4" />
                          LinkedIn Format
                        </button>
                        <button
                          onClick={() => handleCopy(post)}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            copiedId === post.id
                              ? 'bg-mazo-100 text-mazo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {copiedId === post.id ? (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copy Plain
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleExport(post, 'txt')}
                          className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                          <Download className="h-4 w-4" />
                          Export
                        </button>
                        <button
                          onClick={() => setPublishingPost(post)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700"
                        >
                          <Send className="h-4 w-4" />
                          Mark Published
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Published */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-mazo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Published</h2>
              <span className="px-2 py-0.5 bg-mazo-100 text-mazo-700 rounded-full text-sm">
                {publishedPosts.length}
              </span>
            </div>

            {publishedPosts.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed rounded-lg p-8 text-center">
                <p className="text-gray-500">No published posts yet.</p>
              </div>
            ) : (
              <div className="bg-white border rounded-lg divide-y">
                {publishedPosts.map((post) => (
                  <div key={post.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {post.title && (
                            <p className="font-medium text-gray-900">{post.title}</p>
                          )}
                          {post.publish_channel === 'linkedin' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#0A66C2' }}>
                              <Linkedin className="h-3 w-3" />
                              LinkedIn
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 line-clamp-2">{post.body}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          {post.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(post.published_at)}
                            </span>
                          )}
                          {post.linkedin_post_urn && (
                            <a
                              href={`https://www.linkedin.com/feed/update/${post.linkedin_post_urn}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 hover:underline"
                              style={{ color: '#0A66C2' }}
                            >
                              <Linkedin className="h-3 w-3" />
                              View on LinkedIn
                            </a>
                          )}
                          {!post.linkedin_post_urn && post.published_url && (
                            <a
                              href={post.published_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-mazo-600 hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              View on LinkedIn
                            </a>
                          )}
                        </div>
                        {post.publish_notes && (
                          <p className="text-sm text-gray-400 mt-1 italic">
                            Note: {post.publish_notes}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleCopy(post)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        title="Copy"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Publish modal */}
      {publishingPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Mark as Published
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn URL (optional)
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="url"
                    value={linkedInUrl}
                    onChange={(e) => setLinkedInUrl(e.target.value)}
                    placeholder="https://linkedin.com/posts/..."
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={publishNotes}
                  onChange={(e) => setPublishNotes(e.target.value)}
                  placeholder="Any notes about this post..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-500"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setPublishingPost(null);
                  setLinkedInUrl('');
                  setPublishNotes('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                className="flex-1 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700"
              >
                Mark as Published
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Publish Confirmation modal */}
      {confirmPublishPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F1FB' }}>
                <Linkedin className="h-5 w-5" style={{ color: '#0A66C2' }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Publish to LinkedIn
              </h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to publish this post to LinkedIn. This action cannot be undone.
            </p>

            <div className="p-3 bg-gray-50 rounded-lg mb-4 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-700 line-clamp-4 whitespace-pre-wrap">
                {confirmPublishPost.body}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setConfirmPublishPost(null)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const post = confirmPublishPost;
                  setConfirmPublishPost(null);
                  handlePublishToLinkedIn(post);
                }}
                className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: '#0A66C2' }}
              >
                <Linkedin className="h-4 w-4" />
                Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Format modal */}
      {formattingPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0A66C2] rounded-lg flex items-center justify-center">
                <Linkedin className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  LinkedIn Formatted Post
                </h3>
                <p className="text-sm text-gray-500">
                  Optimized for LinkedIn engagement
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto mb-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-mazo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Preview</p>
                    <p className="text-xs text-gray-500">How it will look on LinkedIn</p>
                  </div>
                </div>
                <div className="bg-white rounded p-4 border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap text-sm leading-relaxed">
                    {linkedInFormatted}
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-mazo-50 border border-mazo-200 rounded-lg">
                <p className="text-sm text-mazo-800 flex items-start gap-2">
                  <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    This format includes optimized spacing and structure for LinkedIn.
                    You can further customize it after pasting.
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setFormattingPost(null);
                  setLinkedInFormatted('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCopyFormatted}
                className="flex-1 px-4 py-2 bg-[#0A66C2] text-white rounded-lg hover:bg-[#004182] inline-flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
